import { APP_INITIALIZER, ApplicationConfig, ErrorHandler } from '@angular/core';
import { Router, provideRouter, withComponentInputBinding } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { routes } from './app.routes';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { provideToastr } from 'ngx-toastr';
import { definePreset } from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';
import * as Sentry from '@sentry/angular';
import { environment } from '../environments/environment';

Sentry.init({
  dsn: environment.sentryDsn,
  environment: environment.sentryEnvironment,
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  tracesSampleRate: environment.tracesSampleRate,
  tracePropagationTargets: [
    /^https:\/\/api\.affinity-marketers\.honesty-dev\.ai\/api/,
    /^https:\/\/api\.affinity-marketers\.honesty-stg\.ai\//,
    /^https:\/\/api\.affinity-marketers\.honesty\.ai\//,
  ],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

const AffinityPreset = definePreset(Lara, {
  semantic: {
    primary: {
      50: '#f5f9ff',
      100: '#d0e1fd',
      200: '#abc9fb',
      300: '#85b2f9',
      400: '#609af8',
      500: '#3b82f6',
      600: '#326fd1',
      700: '#295bac',
      800: '#204887',
      900: '#183462',
      950: '#102340',
    },
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '#fafafa',
          100: '#f5f5f5',
          200: '#eeeeee',
          300: '#e0e0e0',
          400: '#bdbdbd',
          500: '#9e9e9e',
          600: '#757575',
          700: '#616161',
          800: '#424242',
          900: '#212121',
          950: '#212121',
        },
        focusRing: {
          shadow: '0 0 0 0.2rem #bfdbfe',
        },
        highlight: {
          background: '#eff6ff',
          focusBackground: 'rgba(59, 130, 246, 0.24)',
          color: '#1d4ed8',
          focusColor: '#1d4ed8',
        },
        formField: {
          background: '#ffffff',
          disabledBackground: '#e9ecef',
          filledBackground: '#f8f9fa',
          filledHoverBackground: '#f8f9fa',
          filledFocusBackground: '#ffffff',
          borderColor: '#ced4da',
          hoverBorderColor: '#3b82f6',
          focusBorderColor: '#3b82f6',
          invalidBorderColor: '#e24c4c',
          color: '#495057',
          disabledColor: '#6c757d',
          placeholderColor: '#6c757d',
          invalidPlaceholderColor: '#e24c4c',
          iconColor: '#6c757d',
          shadow: 'none',
        },
        text: {
          color: '#495057',
          hoverColor: '#343a40',
          mutedColor: '#6c757d',
          hoverMutedColor: '#495057',
        },
        content: {
          background: '#ffffff',
          hoverBackground: '#e9ecef',
          borderColor: '#dee2e6',
          color: '#495057',
          hoverColor: '#495057',
        },
        overlay: {
          select: {
            background: '#ffffff',
            borderColor: '#dee2e6',
            color: '#495057',
          },
          popover: {
            background: '#ffffff',
            borderColor: '#dee2e6',
            color: '#495057',
          },
          modal: {
            background: '#ffffff',
            borderColor: '#dee2e6',
            color: '#495057',
          },
        },
        list: {
          option: {
            focusBackground: '#dee2e6',
            selectedBackground: '#eff6ff',
            selectedFocusBackground: 'rgba(59, 130, 246, 0.24)',
            color: '#495057',
            focusColor: '#495057',
            selectedColor: '#1d4ed8',
            selectedFocusColor: '#1d4ed8',
            icon: {
              color: '#6c757d',
              focusColor: '#495057',
            },
          },
          optionGroup: {
            background: 'transparent',
            color: '#343a40',
          },
        },
        navigation: {
          item: {
            focusBackground: '#e9ecef',
            activeBackground: '#eff6ff',
            color: '#495057',
            focusColor: '#495057',
            activeColor: '#1d4ed8',
            icon: {
              color: '#6c757d',
              focusColor: '#495057',
              activeColor: '#1d4ed8',
            },
          },
          submenuLabel: {
            background: 'transparent',
            color: '#343a40',
          },
          submenuIcon: {
            color: '#6c757d',
            focusColor: '#495057',
            activeColor: '#1d4ed8',
          },
        },
      },
      dark: {
        surface: {
          0: '#0f172a',
          50: '#1e293b',
          100: '#263449',
          200: '#334155',
          300: '#475569',
          400: '#64748b',
          500: '#94a3b8',
          600: '#cbd5e1',
          700: '#e2e8f0',
          800: '#f1f5f9',
          900: '#f8fafc',
          950: '#ffffff',
        },
        focusRing: {
          shadow: '0 0 0 0.2rem rgba(59, 130, 246, 0.4)',
        },
        highlight: {
          background: '#1e3a5f',
          focusBackground: 'rgba(59, 130, 246, 0.32)',
          color: '#93c5fd',
          focusColor: '#bfdbfe',
        },
        formField: {
          background: '#1e293b',
          disabledBackground: '#263449',
          filledBackground: '#263449',
          filledHoverBackground: '#263449',
          filledFocusBackground: '#1e293b',
          borderColor: '#334155',
          hoverBorderColor: '#3b82f6',
          focusBorderColor: '#3b82f6',
          invalidBorderColor: '#f87171',
          color: '#e2e8f0',
          disabledColor: '#64748b',
          placeholderColor: '#94a3b8',
          invalidPlaceholderColor: '#f87171',
          iconColor: '#94a3b8',
          shadow: 'none',
        },
        text: {
          color: '#e2e8f0',
          hoverColor: '#f8fafc',
          mutedColor: '#94a3b8',
          hoverMutedColor: '#cbd5e1',
        },
        content: {
          background: '#1e293b',
          hoverBackground: '#263449',
          borderColor: '#334155',
          color: '#e2e8f0',
          hoverColor: '#f8fafc',
        },
        overlay: {
          select: {
            background: '#1e293b',
            borderColor: '#334155',
            color: '#e2e8f0',
          },
          popover: {
            background: '#1e293b',
            borderColor: '#334155',
            color: '#e2e8f0',
          },
          modal: {
            background: '#1e293b',
            borderColor: '#334155',
            color: '#e2e8f0',
          },
        },
        list: {
          option: {
            focusBackground: '#334155',
            selectedBackground: '#1e3a5f',
            selectedFocusBackground: 'rgba(59, 130, 246, 0.32)',
            color: '#e2e8f0',
            focusColor: '#f8fafc',
            selectedColor: '#93c5fd',
            selectedFocusColor: '#bfdbfe',
            icon: {
              color: '#94a3b8',
              focusColor: '#e2e8f0',
            },
          },
          optionGroup: {
            background: 'transparent',
            color: '#cbd5e1',
          },
        },
        navigation: {
          item: {
            focusBackground: '#334155',
            activeBackground: '#1e3a5f',
            color: '#e2e8f0',
            focusColor: '#f8fafc',
            activeColor: '#93c5fd',
            icon: {
              color: '#94a3b8',
              focusColor: '#e2e8f0',
              activeColor: '#93c5fd',
            },
          },
          submenuLabel: {
            background: 'transparent',
            color: '#cbd5e1',
          },
          submenuIcon: {
            color: '#94a3b8',
            focusColor: '#e2e8f0',
            activeColor: '#93c5fd',
          },
        },
      },
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([authInterceptor])),
    provideAnimations(),
    providePrimeNG({
      theme: {
        preset: AffinityPreset,
        options: {
          darkModeSelector: '.dark',
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, primeng, tailwind-utilities',
          },
        },
      },
    }),
    provideToastr({
      timeOut: 5000,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
    }),
    MessageService,
    ConfirmationService,
    DialogService,
    DynamicDialogRef,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
};
